import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import DocumentFieldEvidenceCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/EvidenceCards/DocumentFieldEvidenceCard';
import FactEvidenceCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/EvidenceCards/FactEvidenceCard';
import ImportedDocumentEvidenceCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/EvidenceCards/ImportedDocumentEvidenceCard';
import TurnEvidenceCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/EvidenceCards/TurnEvidenceCard';
import type { DocumentEvidenceView } from '@UI/Store/Documents/Views/buildDocumentEvidenceView';
import { css } from '@emotion/react';
import { Button } from '@verclam/ui-kit';
import { ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const titleStyle = css`
	${theme.typography.eyebrow}
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const listStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const toggleStyle = css`
	align-self: flex-start;
`;

const emptyStyle = css`
	margin: 0;
	padding: 0.875rem 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly evidence: readonly DocumentEvidenceView[];
};

/** The sources a value comes from, one card per source, each card owning its own "Ouvrir …" link. */
const FieldSources: React.FC<Props> = ({ evidence }) => {
	const listId = React.useId();
	const [isExpanded, setIsExpanded] = React.useState(false);

	const handleToggleClick = React.useCallback(() => {
		setIsExpanded((current) => !current);
	}, []);

	return (
		<section css={sectionStyle} aria-label="Sources">
			<div css={titleStyle}>Sources · {evidence.length}</div>
			<ConditionalLoader
				data={evidence.length > 0}
				fallback={
					<p css={emptyStyle}>Aucune source : rien dans les entretiens ne traite ce point.</p>
				}
			>
				<div id={listId} css={listStyle}>
					<ConditionalLoader data={isExpanded}>
						{evidence.map((entry) => {
							switch (entry.type) {
								case 'turn':
									return (
										<TurnEvidenceCard
											key={`${entry.interviewId}-${entry.turnPosition}`}
											evidence={entry}
										/>
									);
								case 'fact':
									return <FactEvidenceCard key={entry.key} evidence={entry} />;
								case 'document_field':
									return (
										<DocumentFieldEvidenceCard
											key={`document-${entry.documentId}-${entry.key}`}
											evidence={entry}
										/>
									);
								case 'imported_document':
									return (
										<ImportedDocumentEvidenceCard
											key={`imported-document-${entry.importedDocumentId}`}
											evidence={entry}
										/>
									);
								default: {
									const unreachable: never = entry;

									return unreachable;
								}
							}
						})}
					</ConditionalLoader>
				</div>
				<Button
					type="button"
					css={toggleStyle}
					label={isExpanded ? 'Voir moins' : 'Voir plus'}
					icon={isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
					text
					severity="secondary"
					aria-expanded={isExpanded}
					aria-controls={listId}
					onClick={handleToggleClick}
				/>
			</ConditionalLoader>
		</section>
	);
};

export default FieldSources;
